import { useState, useEffect } from 'react';
import {
  Save,
  AlertTriangle,
  CheckCircle,
  Info,
  Award,
  UserX,
  ClipboardList,
  BookOpen,
  Eye,
} from 'lucide-react';
import {
  getAcademicParameters,
  saveAcademicParameters,
  type AcademicParameters,
} from '@/services/academicManagementService';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';

const defaultParameters: Partial<AcademicParameters> = {
  // Grading Configuration
  passingGrade: 10,
  maxGrade: 20,
  gradeDecimals: 2,

  // Subject Validation Configuration
  subjectValidationMethod: 'AVERAGE',
  minScorePerComponent: 5,

  // Academic Validation Configuration
  semesterValidationMethod: 'AVERAGE',
  yearValidationMethod: 'ALL_VALIDATED',

  // Rattrapage Configuration
  rattrapageEnabled: true,
  rattrapageMaxAttempts: 1,
  rattrapageGradeCap: 12,

  // Absence Configuration
  maxAbsencesBeforeAlert: 3,
  maxAbsencesBeforeRattrapage: 5,
  lateCountsAsAbsence: false,
  lateThresholdMinutes: 15,
};

export default function ParametresAcademiquesPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.ACADEMIC_CONFIG_MANAGE);

  const [parameters, setParameters] = useState<Partial<AcademicParameters>>(defaultParameters);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadParameters();
  }, []);

  const loadParameters = async () => {
    try {
      setLoading(true);
      const data = await getAcademicParameters();
      if (data) {
        setParameters(data);
      }
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement des paramètres';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof AcademicParameters, value: unknown) => {
    setParameters((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveAcademicParameters(parameters);
      setSuccess(true);
      setHasChanges(false);
      setError(null);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">Succès</p>
            <p className="text-sm text-green-600">Les paramètres ont été sauvegardés avec succès.</p>
          </div>
        </div>
      )}

      {!canEdit && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Eye className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Mode lecture seule</p>
            <p className="text-sm text-blue-600">
              Vous pouvez consulter les paramètres mais vous n'avez pas la permission de les modifier.
            </p>
          </div>
        </div>
      )}

      {/* Grading Parameters */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Award className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Paramètres de notation</h2>
            <p className="text-sm text-gray-500">Configuration des notes</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note de passage
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="20"
                step="0.5"
                value={parameters.passingGrade || 10}
                onChange={(e) => handleChange('passingGrade', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!canEdit}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">/20</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note maximale
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={parameters.maxGrade || 20}
              onChange={(e) => handleChange('maxGrade', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Décimales
            </label>
            <select
              value={parameters.gradeDecimals || 2}
              onChange={(e) => handleChange('gradeDecimals', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            >
              <option value={0}>0 (entier)</option>
              <option value={1}>1 décimale</option>
              <option value={2}>2 décimales</option>
            </select>
          </div>
        </div>
      </div>

      {/* Subject Validation */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-100 rounded-lg">
            <BookOpen className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Validation de la matière</h2>
            <p className="text-sm text-gray-500">Méthode de validation pour chaque matière</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Méthode de validation
            </label>
            <select
              value={parameters.subjectValidationMethod || 'AVERAGE'}
              onChange={(e) => handleChange('subjectValidationMethod', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            >
              <option value="AVERAGE">Moyenne générale de la matière</option>
              <option value="ALL_COMPONENTS">Tous les composants validés</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {parameters.subjectValidationMethod === 'ALL_COMPONENTS'
                ? 'L\'étudiant doit valider chaque composant pour valider la matière'
                : 'L\'étudiant valide si sa moyenne est >= note de passage'}
            </p>
          </div>

          {parameters.subjectValidationMethod === 'ALL_COMPONENTS' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note minimale par composant
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="20"
                  step="0.5"
                  value={parameters.minScorePerComponent || 5}
                  onChange={(e) => handleChange('minScorePerComponent', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">/20</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Note minimale requise pour chaque composant</p>
            </div>
          )}
        </div>
      </div>

      {/* Academic Validation */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Validation académique</h2>
            <p className="text-sm text-gray-500">Règles de validation du semestre et de l'année</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Validation du semestre
            </label>
            <select
              value={parameters.semesterValidationMethod || 'AVERAGE'}
              onChange={(e) => handleChange('semesterValidationMethod', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            >
              <option value="AVERAGE">Moyenne générale</option>
              <option value="ALL_VALIDATED">Tous les modules validés</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Validation de l'année
            </label>
            <select
              value={parameters.yearValidationMethod || 'ALL_VALIDATED'}
              onChange={(e) => handleChange('yearValidationMethod', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            >
              <option value="AVERAGE">Moyenne générale</option>
              <option value="ALL_VALIDATED">Tous les semestres validés</option>
            </select>
          </div>
        </div>
      </div>

      {/* Absence Parameters */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-orange-100 rounded-lg">
            <UserX className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Paramètres d'absences</h2>
            <p className="text-sm text-gray-500">Configuration des seuils d'absences</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Seuil d'alerte absences
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={parameters.maxAbsencesBeforeAlert || 3}
              onChange={(e) => handleChange('maxAbsencesBeforeAlert', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            />
            <p className="text-xs text-gray-500 mt-1">Nombre d'absences avant alerte au professeur</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Absences avant rattrapage
            </label>
            <input
              type="number"
              min="1"
              max="30"
              value={parameters.maxAbsencesBeforeRattrapage || 5}
              onChange={(e) => handleChange('maxAbsencesBeforeRattrapage', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            />
            <p className="text-xs text-gray-500 mt-1">Nombre d'absences avant passage en rattrapage</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Seuil de retard signalé (minutes)
            </label>
            <input
              type="number"
              min="5"
              max="120"
              value={parameters.lateThresholdMinutes || 15}
              onChange={(e) => handleChange('lateThresholdMinutes', parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            />
            <p className="text-xs text-gray-500 mt-1">Seuil pour signaler dans les statistiques</p>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={parameters.lateCountsAsAbsence || false}
              onChange={(e) => handleChange('lateCountsAsAbsence', e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={!canEdit}
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Retards comptent comme absences</span>
              <p className="text-xs text-gray-500">Comptabiliser les retards dans le total des absences</p>
            </div>
          </label>
        </div>
      </div>

      {/* Rattrapage Parameters */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <ClipboardList className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Paramètres de rattrapage</h2>
            <p className="text-sm text-gray-500">Configuration des examens de rattrapage</p>
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer mb-6">
          <input
            type="checkbox"
            checked={parameters.rattrapageEnabled ?? true}
            onChange={(e) => handleChange('rattrapageEnabled', e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={!canEdit}
          />
          <div>
            <span className="text-sm font-medium text-gray-900">Système de rattrapage activé</span>
            <p className="text-xs text-gray-500">Permettre les examens de rattrapage pour les étudiants</p>
          </div>
        </label>

        {parameters.rattrapageEnabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tentatives max
              </label>
              <input
                type="number"
                min="1"
                max="3"
                value={parameters.rattrapageMaxAttempts || 1}
                onChange={(e) => handleChange('rattrapageMaxAttempts', parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!canEdit}
              />
              <p className="text-xs text-gray-500 mt-1">Nombre maximum de tentatives de rattrapage</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plafond de note rattrapage
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="20"
                  step="0.5"
                  value={parameters.rattrapageGradeCap ?? 12}
                  onChange={(e) => handleChange('rattrapageGradeCap', e.target.value ? parseFloat(e.target.value) : null)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  disabled={!canEdit}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">/20</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Note maximale après rattrapage (s'applique à tous les cas)</p>
            </div>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-blue-800">Information</p>
          <p className="text-sm text-blue-600">
            Ces paramètres s'appliquent à l'ensemble de l'établissement. Les modifications prendront effet immédiatement
            pour tous les calculs de notes, validations et rattrapages.
          </p>
        </div>
      </div>

      {/* Save button */}
      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${
              hasChanges
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Sauvegarder les paramètres
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
